/**
 * FolderPickerModal Component
 * Modal for selecting a folder destination (move/copy operations)
 */

import { html, attr, on, Signal, ForEach, When, type Value } from '@tempots/dom'
import { Button, ButtonVariant, Icon } from '@tempots/beatui'
import { LoadingState } from '@/components/ui'
import type { FolderResponse } from '@/services/types'

// ============================================================================
// Types
// ============================================================================

export interface FolderPickerModalProps {
  /** Title of the modal */
  title: string
  /** Message displayed below the title */
  message: Value<string>
  /** List of available folders */
  availableFolders: Signal<FolderResponse[]>
  /** Whether folders are loading */
  loadingFolders: Signal<boolean>
  /** Whether an action is being performed */
  performingAction: Signal<boolean>
  /** Error message from the action */
  actionError: Signal<string | null>
  /** Called when a folder is selected */
  onSelect: (folderId: string) => void
  /** Called when the modal is closed */
  onClose: () => void
}

// ============================================================================
// Component
// ============================================================================

/**
 * FolderPickerModal - Modal for selecting a folder
 *
 * @example
 * ```typescript
 * When(showMoveModal, () =>
 *   FolderPickerModal({
 *     title: 'Move Files',
 *     message: `Move ${count} files to:`,
 *     availableFolders,
 *     loadingFolders,
 *     performingAction,
 *     actionError,
 *     onSelect: handleMove,
 *     onClose: () => showMoveModal.set(false),
 *   })
 * )
 * ```
 */
export function FolderPickerModal({
  title,
  message,
  availableFolders,
  loadingFolders,
  performingAction,
  actionError,
  onSelect,
  onClose,
}: FolderPickerModalProps) {
  const hasActionError = actionError.map((e) => e !== null)

  return html.div(
    attr.class(
      'fixed inset-0 bg-black/50 flex items-center justify-center z-50'
    ),
    on.click(onClose),
    html.div(
      attr.class(
        'bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col'
      ),
      on.click((e) => e.stopPropagation()),
      // Modal header
      html.div(
        attr.class(
          'flex items-center justify-between px-6 py-4 border-b border-gray-200'
        ),
        html.h3(attr.class('text-lg font-semibold text-gray-900'), title),
        html.button(
          attr.type('button'),
          attr.class('text-gray-400 hover:text-gray-600'),
          on.click(onClose),
          Icon({ icon: 'mdi:close' }, attr.class('w-5 h-5'))
        )
      ),
      // Modal body
      html.div(
        attr.class('flex-1 overflow-y-auto p-6'),
        // Message
        html.p(attr.class('text-sm text-gray-600 mb-4'), message),
        // Error message
        When(hasActionError, () =>
          html.div(
            attr.class(
              'mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm whitespace-pre-wrap'
            ),
            actionError
          )
        ),
        // Loading
        When(loadingFolders, () => LoadingState({ size: 'md' })),
        // Folder list
        When(
          loadingFolders.map((l) => !l),
          () =>
            html.div(
              attr.class('space-y-2'),
              // Root option
              html.button(
                attr.type('button'),
                attr.class(
                  performingAction.map((m) =>
                    m
                      ? 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg text-left opacity-50 cursor-not-allowed'
                      : 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-left transition-colors'
                  )
                ),
                attr.disabled(performingAction),
                on.click(() => onSelect('')),
                Icon(
                  { icon: 'mdi:folder-home' },
                  attr.class('w-5 h-5 text-gray-400')
                ),
                html.span(
                  attr.class('text-sm font-medium text-gray-900'),
                  'Root (All Files)'
                )
              ),
              // Folder options
              ForEach(availableFolders, (folder) =>
                html.button(
                  attr.type('button'),
                  attr.class(
                    performingAction.map((m) =>
                      m
                        ? 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg text-left opacity-50 cursor-not-allowed'
                        : 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-left transition-colors'
                    )
                  ),
                  attr.disabled(performingAction),
                  on.click(() => onSelect(folder.get().id)),
                  Icon(
                    { icon: 'mdi:folder' },
                    attr.class('w-5 h-5 text-gray-400')
                  ),
                  html.span(
                    attr.class('text-sm font-medium text-gray-900'),
                    folder.map((f) => f.name)
                  )
                )
              )
            )
        )
      ),
      // Modal footer
      html.div(
        attr.class('flex justify-end gap-3 px-6 py-4 border-t border-gray-200'),
        Button(
          {
            variant: 'ghost' as ButtonVariant,
            onClick: onClose,
          },
          'Cancel'
        )
      )
    )
  )
}
